import React, { useEffect, useState } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastData {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ToastProps extends ToastData {
  onDismiss: (id: string) => void;
}

const typeConfig: Record<ToastType, { icon: React.ElementType; bgColor: string; borderColor: string }> = {
  success: {
    icon: CheckCircle,
    bgColor: 'bg-spotify-green',
    borderColor: 'border-green-400',
  },
  error: {
    icon: AlertCircle,
    bgColor: 'bg-red-600',
    borderColor: 'border-red-400',
  },
  info: {
    icon: Info,
    bgColor: 'bg-blue-600',
    borderColor: 'border-blue-400',
  },
  warning: {
    icon: AlertTriangle,
    bgColor: 'bg-yellow-600',
    borderColor: 'border-yellow-400',
  },
};

export const Toast: React.FC<ToastProps> = ({
  id,
  message,
  type,
  duration = 4000,
  action,
  onDismiss,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  const config = typeConfig[type];
  const Icon = config.icon;

  useEffect(() => {
    // Trigger enter animation
    const enterTimer = setTimeout(() => setIsVisible(true), 10);

    // Auto dismiss
    const dismissTimer = setTimeout(() => {
      handleDismiss();
    }, duration);

    return () => {
      clearTimeout(enterTimer);
      clearTimeout(dismissTimer);
    };
  }, [duration]);

  const handleDismiss = () => {
    setIsLeaving(true);
    setTimeout(() => onDismiss(id), 300);
  };

  return (
    <div
      className={`
        flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg
        ${config.bgColor} text-white
        transform transition-all duration-300 ease-out
        ${isVisible && !isLeaving ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}
        min-w-[300px] max-w-[500px]
      `}
      role="alert"
      aria-live="polite"
    >
      <Icon className="w-5 h-5 flex-shrink-0" />

      <p className="flex-1 text-sm font-medium">{message}</p>

      {action && (
        <button
          onClick={() => {
            action.onClick();
            handleDismiss();
          }}
          className="px-3 py-1 text-sm font-semibold bg-white/20 hover:bg-white/30 rounded-full transition-colors"
        >
          {action.label}
        </button>
      )}

      <button
        onClick={handleDismiss}
        className="p-1 hover:bg-white/20 rounded-full transition-colors"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default Toast;
